<template>
  <div>
    <div class="container bg-white m-auto py-4 px-8 max-w-screen-sm">
      <div class="image-container max-w-screen-sm">
          <img class="m-auto mb-8 w-64" alt='Connections App' src='../../assets/images/logo.png'>
        </div>
      <div v-if="hasToken">
        <div class="header">
          <p class="my-4">
            <span class="font-bold text-base">{{instanceName}}</span>
            <br>
            The Connections App offers community, hope, motivation, skill-building, recovery tracking, and, if needed, crisis support, to help individuals achieve their recovery goals.
          </p>
          <p class="my-4">This app is free; your privacy is protected.</p>
          <p class="mt-4">Fill in the form below and you will receive a text message invitation with a download link and temporary password.</p>
        </div>
        <div class="form text-left max-w-screen-sm m-auto py-4">
          <!-- First Name -->
          <div class="my-4">
            <label class="required">{{patientData.first_name.label}}</label>
            <input class="w-full" name="first_name" type="text" v-model="patientData.first_name.value" />
          </div>
          <!-- Last Name -->
          <div class="my-4">
            <label class="required">{{patientData.last_name.label}}</label>
            <input class="w-full" name="last_name" type="text" v-model="patientData.last_name.value" />
          </div>
          <!-- Gender -->
          <div class="my-4">
            <label class="required">{{patientData.gender_id.label}}</label>
            <select class="w-full bg-white" name="gender_id" v-model="patientData.gender_id.value">
              <option value=""></option>
              <option value="1">Male</option>
              <option value="2">Female</option>
              <option value="4">Transgender</option>
              <option value="5">Non-binary</option>
              <option value="3">Prefer not to respond</option>
            </select>
          </div>
          <!-- Date of Birth -->
          <div class="my-4">
            <div class="flex">
              <label class="required">{{patientData.dob.label}}</label>
              <div class="text-red-500 ml-auto">Required for first-time login</div>
            </div>
            <input class="ios bg-white w-full" name="dob" type="date" @blur="checkDOB"  v-model="patientData.dob.value" />
          </div>
          <!-- Mobile Phone Number -->
          <div class="my-4">
            <label class="required">{{patientData.mobile.label}}</label>
            <div class="flex">
              <div class="country-code-picker mr-2">
                <div @click="openOptions" class="selectedValue">
                  <div class="flag"
                    v-bind:class="{ 'us': selectedCode.country === 'us', 'nz': selectedCode.country === 'nz' }"></div>
                  <div class="code">{{selectedCode.code}}</div>
                </div>
                <div v-if="optionsOpen" class="options">
                  <div class="option cursor-pointer" v-for="option in countryCodeOptions" :key="option.code" @click="selectOption(option)">
                    <div class="flag"
                      v-bind:class="{ 'us': option.country === 'us', 'nz': option.country === 'nz' }"></div>
                    <div class="code">{{option.code}}</div>
                  </div>
                </div>
              </div>
              <input class="w-full" name="phoneNumber" :placeholder="placeholderNumber" :mask="phoneNumberMask" type="text" @change="changePhone" v-model="phoneNumber" />
            </div>
          </div>
          <!-- Email -->
          <div class="my-4">
            <div class="flex">
              <label>{{patientData.email.label}} <span class="optional">(optional)</span></label>
            </div>
            <input class="w-full" name="email" type="text" v-model="patientData.email.value" />
          </div>
          <!-- Payer Name -->
          <div class="my-4" v-if="showPayerName">
            <label class="required">{{patientData.payer_name_id.label}}</label>
            <select class="w-full bg-white" name="payer_name_id" v-model="patientData.payer_name_id.value">
              <option value=""></option>
              <option v-for="payer in payerNames" :value="payer.Payer_Name_ID" :key="payer.Payer_Name_ID">{{payer.Value}}</option>
            </select>
          </div>
          <div class="w-full text-center mb-4">
            <p class="my-4">Clicking Submit will generate a SMS text message to the individual with an invitation to use the Connections App in the form of a download link and temporary password. By agreeing to receive the invitation, the individual permits CHESS Health to use text messages or phone calls to offer technical and login assistance.</p>
            <button id="submit" class="w-24" @click="submitForm">Submit</button>
          </div>
        </div>
        <div class="footer pb-4">
          <p class="mb-2">Have questions? Need help? Email <a href="mailto:support@connectionsapp.com">support@connectionsapp.com</a></p>
          <p><a href="https://www.chess.health/privacy-policy/" target="_blank">Privacy Policy</a></p>
        </div>
      </div>
      <div v-if="!hasToken">
        <p class="my-4 font-bold text-base">Something went wrong.</p>
        <p class="my-4">Have questions? Need help? Email <a href="mailto:support@connectionsapp.com">support@connectionsapp.com</a></p>
      </div>
    </div>
    <div ref="modalContainer"></div>
    <LoadingUI msg="Setting up account..." v-if="loading" />
  </div>
</template>

<script>
import Vue from 'vue'
import SuccessModal from '../Self-Signup/SuccessModal.vue'
import LoadingUI from '../LoadingUI.vue'
import BirthValidationModal from '../Self-Signup/BirthValidationModal.vue'
import { createPatient, createCommunityUser, getPayerNames } from '../../services/ssu-service'
import config from '../../config'

export default {
  name: 'Home',
  components: { // imported components
    LoadingUI
  },
  props: { // props passed into component
    //msg: String
  },
  data() { // component props
    return {
      hasToken: false,
      loading: false,
      modalOpen: false,
      optionsOpen: false,
      selectedCode: { code: '+1', country: 'us' },
      countryCodeOptions: [
        { code: '+1', country: 'us' },
        { code: '+64', country: 'nz' }
      ],
      patientData: {
        first_name: { label: 'First Name', value: '', required: true },
        last_name: { label: 'Last Name', value: '', required: true },
        dob: { label: 'Date of Birth', value: '', required: true },
        mobile: { label: 'Mobile Phone Number', value: '', required: true },
        gender_id: { label: 'Gender', value: '', required: true },
        email: { label: 'Email Address', value: '', required: false }
      },
      phoneNumber: '',
      placeholderNumber: '(123)-456-7890',
      phoneNumberMask: '(000)-000-0000',
      instanceName: config.instance,
      showPayerName: config.payer_name,
      payerNames: []
    }
  },
  created() { /* Called synchronously after the instance is created */
    let urlParams = window.location.search.split('token=');
    let token = urlParams[urlParams.length - 1];
    if (token) {
      sessionStorage.setItem('token', token);
      this.hasToken = true;
    }
    // add field specific to WV
    if (this.showPayerName) {
      this.patientData['payer_name_id'] = { label: 'Insurance Provider', value: '', required: true };
      // get payer names
      getPayerNames().then(res => {
        if (res.success) {
          this.payerNames = res.data;
        }
      });
    }
    //if Timezone is a new Zealand timezone, default the country code to new Zealand's
    if (Intl.DateTimeFormat().resolvedOptions().timeZone == 'Pacific/Chatham' || Intl.DateTimeFormat().resolvedOptions().timeZone == 'Pacific/Auckland' ){
       this.selectedCode = { code: "+64", country: "nz" };
    }
  },
  mounted() { /* Called after the instance has been mounted */},
  methods: { // component functions
    openOptions() {
      this.optionsOpen = true;
    },
    selectOption(option) {
      this.selectedCode = option;
      this.patientData.mobile.value = this.selectedCode.code + this.phoneNumber;
      this.optionsOpen = false;
      if (this.selectedCode.country === 'us') {
        this.placeholderNumber = '(123)-456-7890';
        this.phoneNumberMask = '(000)-000-0000';
      } else {
        this.placeholderNumber = '000000000000000';
        this.phoneNumberMask = '000000000000000';
      }
    },
    changePhone() {
      this.patientData.mobile.value = this.selectedCode.code + this.phoneNumber;
    },
    checkDOB(){
      const today = new Date()
      const birthDate = new Date(this.patientData.dob.value)
      if((today.getFullYear() - birthDate.getFullYear()) >= 100){
        this.createValidationModal();
      }

      let age = today.getFullYear() - birthDate.getFullYear();
      const month_diff = today.getMonth() - birthDate.getMonth();
      const day_diff = today.getDate() - birthDate.getDate();

      if (month_diff < 0 || (month_diff === 0 && day_diff < 0)) {
        age--;
      }

      if (age < 13) {
        this.createValidationModal();
      }
    },
    submitForm() {
      const today = new Date()
      const birthDate = new Date(this.patientData.dob.value)
      let age = today.getFullYear() - birthDate.getFullYear();
      const month_diff = today.getMonth() - birthDate.getMonth();
      const day_diff = today.getDate() - birthDate.getDate();
      if (month_diff < 0 || (month_diff === 0 && day_diff < 0)) {
        age--;
      }
      if (age < 13) {
        this.$toastr.Add({
          title: "Age Error",
          msg: "Patient under 13 years old cannot be created.",
          type: "error",
          position: "toast-bottom-right",
          style: { "width": "auto", "min-width": "300px" }
        });
        return; 
      }

      // bind field values to model first to make sure values are populated
      this.bindFields();
      let invalid = this.invalidFields();
      if (invalid.length) {
        // build error message
        let message = '';
        invalid.forEach(field => {
          message += `- ${this.patientData[field].label} is invalid<br>`;
        });
        this.$toastr.Add({
          title: "Invalid Fields",
          msg: message,
          type: "error",
          position: "toast-bottom-right",
          style: { "width": "auto", "min-width": "300px" }
        });
      } else {
        const toastOptions = {
          title: "Error",
          msg: "There was a problem submitting the form.<br>Please contact support@connectionsapp.com for assistance.",
          type: "error",
          position: "toast-bottom-right",
          style: { "width": "430px" }
        };
        this.loading = true;
        // create patient record
        let data = this.requestData();
        createPatient(data).then(res => {
          if (res.success) {
            // assign patient data required for community POST
            data['sso_id'] = res.data.SSO_ID;
            data['user_id'] = res.data.User_ID;
            data['user_guid'] = res.data.patient_guid;
            data['temp_pass'] = res.data.temp_pass;
            data['alias'] = this.generateCommunityAlias();
            // create new instance of the modal
            this.createSuccessModal(data);
            // create commmunity user
            createCommunityUser(data).then(res => {
              if (res.success) {
                this.loading = false;
                this.$modal.show('success');
                // clear patient data
                this.resetPatientData();
              } else {
                this.loading = false;
                this.$toastr.Add(toastOptions);
              }
            }).catch(err => {
              console.error(err);
              this.loading = false;
              this.$toastr.Add(toastOptions);
            });
          } else {
            this.loading = false;
            this.$toastr.Add(toastOptions);
          }
        }).catch(err => {
          console.error(err);
          this.loading = false;
          this.$toastr.Add(toastOptions);
        });
      }
    },
    resetPatientData() {
      this.patientData = {
        first_name: { label: 'First Name', value: '', required: true },
        last_name: { label: 'Last Name', value: '', required: true },
        dob: { label: 'Date of Birth', value: '', required: true },
        mobile: { label: 'Mobile Phone Number', value: '', required: true },
        gender_id: { label: 'Gender', value: '', required: true },
        email: { label: 'Email Address', value: '', required: false }
      };
      // clear field specific to WV
      if (this.showPayerName) {
        this.patientData['payer_name_id'] = { label: 'Insurance Provider', value: '', required: true };
      }
      this.phoneNumber = '';
    },
    requestData() {
      let obj = {};
      let keys = Object.keys(this.patientData);
      let values = Object.values(this.patientData);
      values.forEach((v, i) => {
        obj[keys[i]] = v.value;
      });
      return obj;
    },
    invalidFields() {
      let fields = [];
      let keys = Object.keys(this.patientData);
      let values = Object.values(this.patientData);
      let empty = values.map((v, i) => { 
        return { value: v.value, index: i, required: v.required }
      }).filter(v => !v.value && v.required);
      if (empty.length > 0) {
        empty.forEach(e => {
          let key = keys[e.index];
          fields.push(key);
        });
        // special case for phone number
        if (!fields.includes('mobile') && (!this.phoneNumber || !this.validPhoneNumber())) {
          fields.push('mobile');
        }
      }
      return fields;
    },
    bindFields() {
      let keys = Object.keys(this.patientData);
      keys.forEach(key => {
        let field = document.getElementsByName(key)[0];
        if (field) {
          this.patientData[key].value = field.value;
        }
      });
    },
    validPhoneNumber() {
      // get all numbers in a string
      var numbers = this.phoneNumber.toString().trim().replace(/\D/g, '');
      switch (this.selectedCode.country) {
        case 'us':
          if (numbers.length === 10) {
            return true;
          }
          return false;
        case 'nz':
          if (numbers.length) {
            return true;
          }
          return false;
      }
    },
    generateCommunityAlias() {
      var stripped_number = this.patientData.mobile.value.replace(/[^0-9.]/g, '');
      var dob = new Date(this.patientData.dob.value);
      var day = dob.getDate().toString();
      var month = (dob.getMonth() + 1).toString();
      if (day.length === 1) {
        day = `0${day}`;
      }
      if (month.length === 1) {
        month = `0${month}`;
      }
      var temp_username = `${month}${day}${dob.getFullYear().toString()}${stripped_number}`;
      return temp_username;
    },
    createSuccessModal(patient) {
      let ModalComponent = Vue.extend(SuccessModal);
      let instance = new ModalComponent({
        propsData: { patient }
      });
      instance.$mount();
      this.$refs.modalContainer.innerHTML = '';
      this.$refs.modalContainer.appendChild(instance.$el);
    },
    createValidationModal(){
      let ModalComponent = Vue.extend(BirthValidationModal);
      let instance = new ModalComponent()
      instance.$mount();
      this.$refs.modalContainer.innerHTML = '';
      this.$refs.modalContainer.appendChild(instance.$el);
    }
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
  .container {
    border-radius: 8px;
    margin-top: 50px;
    overflow: hidden;
    box-shadow: 1px 5px 15px #9c9c9c;
  }
  label {
    display: block;
    font-size: 13px;
    text-align: left;
    margin-bottom: 8px;
    color: #597495;
    font-weight: 700;
  }
    label.required::after {
      content: "*";
      color: red;
      padding-left: 3px;
    }
    label .optional {
      color: #a0a0a0;
      font-size: 12px;
    }
  input, select {
    font-size: 14px;
    font-weight: 400;
    color: #333;
    height: 40px;
    border-radius: 4px;
    padding: 6px 12px;
    line-height: 1.42857143;
    border: 1px solid #ccc;
    -webkit-box-shadow: inset 0 1px 1px rgb(0 0 0 / 8%);
    box-shadow: inset 0 1px 1px rgb(0 0 0 / 8%);
    -webkit-transition: border-color ease-in-out .15s,-webkit-box-shadow ease-in-out .15s;
    -o-transition: border-color ease-in-out .15s,box-shadow ease-in-out .15s;
    transition: border-color ease-in-out .15s,box-shadow ease-in-out .15s;
  }
    input:focus, select:focus {
      border-color: #66afe9;
      outline: 0;
      -webkit-box-shadow: inset 0 1px 1px rgb(0 0 0 / 8%), 0 0 8px rgb(102 175 233 / 60%);
      box-shadow: inset 0 1px 1px rgb(0 0 0 / 8%), 0 0 8px rgb(102 175 233 / 60%);
    }
  button {
    height: 40px;
    font-size: 14px;
    color: #fff;
    border: none;
    padding: 6px 12px;
    border-radius: 3px;
    background: #750097;
  }
  a {
    color: #750097;
  }
  .us {
    background-image: url("../../assets/images/us_flag.png");
  }
  .nz {
    background-image: url("../../assets/images/nz_flag.png");
  }
  .flag {
    display: inline-block;
    height: 12px;
    width: 17px;
  }
  .code {
    display: inline-block;
  }
  .country-code-picker {
    position: relative;
    width: 50px;
    height: 40px;
    border-radius: 4px;
    border: 1px solid #ccc;
  }
  .selectedValue {
    width: 50px;
    height: 100%;
    cursor: pointer;
    padding: 10px 0px;
    text-align: center;
    border-radius: 4px;
  }
  .options {
    position: absolute;
    background: white;
    border: 1px solid #ccc;
    width: 50px;
    top: -1px;
    left: -1px;
    border-radius: 4px;
    z-index: 2;
  }
  .option {
    padding: 2px 3px;
  }

  .ios{
     -webkit-appearance: none;
    -moz-appearance: none;
  }
</style>
